'use client';

import { dateLocaleFor, type Translator } from '@/i18n';
import { useT } from '@/lib/locale-context';
import { useNow } from './use-now';
import styles from './relative-time.module.css';

/**
 * Client-hydrated timestamp display: the server (and the first client render)
 * can only know the timezone-neutral instant, not the viewer's clock or
 * locale, so both variants below paint an ISO string first and swap to a
 * locale-formatted one after mount - no hydration mismatch is possible
 * because the first client render matches the server's markup exactly.
 *
 * Two variants share this rule: {@link RelativeTime} (relative label - "3h",
 * "2d" - for feed-shaped surfaces where recency matters) and {@link DateOnly}
 * (day-grain locale date - for a column/field that names a specific day, not
 * "how long ago"). The words come from the catalog in the page's locale
 * (docs/I18N.md); a date formats in the browser's own language where it is the
 * page's language, so its region still shapes the date (`dateLocaleFor`), and
 * in the page's locale otherwise. Both read the kit's one clock,
 * `useNow`: null on the server and the first client paint (the ISO face),
 * the client's time after mount.
 */

/** A timestamp that is correct on both server and client. The relative label
 *  ("2h") and the localized title depend on the client's clock and locale,
 *  which the server cannot know, so the server (and first client render)
 *  show a stable ISO date; the client swaps in relative time. */
export function RelativeTime({ at, className }: { at: Date; className?: string }) {
  const iso = at.toISOString();
  const t = useT();
  // Ticks every minute, so "now" becomes "1m" without a reload.
  const now = useNow();
  const display =
    now === null
      ? { text: iso.slice(0, 10), title: iso }
      : {
          text: relativeTime(at, now, t),
          title: at.toLocaleString(dateLocaleFor(t.locale, navigator.languages)),
        };
  return (
    <time
      className={className ? `${styles.time} ${className}` : styles.time}
      dateTime={iso}
      title={display.title}
    >
      {display.text}
    </time>
  );
}

/** Compact relative time ("now", "5m", "3h", "2d", "4w"), then a date. Runs
 *  after mount only, so `navigator` is there. */
function relativeTime(at: Date, now: number, t: Translator): string {
  const ms = now - at.getTime();
  const s = Math.round(ms / 1000);
  if (s < 45) return t('kit.relativeTime.now');
  const m = Math.round(s / 60);
  if (m < 60) return t('kit.relativeTime.minutes', { count: m });
  const h = Math.round(m / 60);
  if (h < 24) return t('kit.relativeTime.hours', { count: h });
  const d = Math.round(h / 24);
  if (d < 7) return t('kit.relativeTime.days', { count: d });
  const w = Math.round(d / 7);
  if (w < 5) return t('kit.relativeTime.weeks', { count: w });
  return at.toLocaleDateString(dateLocaleFor(t.locale, navigator.languages), {
    month: 'short',
    day: 'numeric',
  });
}

/** A day-grain, locale-formatted date (no relative label): for a column or
 *  field that names a specific day (Updated / Created / declared-on), where a
 *  locale date beats both raw ISO and a decaying "3d ago" label. The exact
 *  instant still rides the `title` attribute. */
export function DateOnly({
  at,
  className,
  options,
}: {
  at: Date;
  className?: string;
  /** A narrower face (e.g. `{ day: 'numeric', month: 'numeric' }` for an axis
   *  label); the title then carries the full date instead of the instant. */
  options?: Intl.DateTimeFormatOptions;
}) {
  const iso = at.toISOString();
  const { locale } = useT();
  // The mount only: a day-grain face needs the client's locale, not a tick.
  const mounted = useNow(null) !== null;
  const tag = mounted ? dateLocaleFor(locale, navigator.languages) : locale;
  const display = !mounted
    ? { text: iso.slice(0, 10), title: iso }
    : options
      ? {
          text: at.toLocaleDateString(tag, options),
          title: at.toLocaleDateString(tag, { dateStyle: 'full' }),
        }
      : { text: at.toLocaleDateString(tag), title: at.toLocaleString(tag) };
  return (
    <time className={className} dateTime={iso} title={display.title}>
      {display.text}
    </time>
  );
}
